<template>
  <div>
    <div class="container-fluid">
      <!-- ============================================================== -->
      <!-- Bread crumb and right sidebar toggle -->
      <!-- ============================================================== -->
      <div class="row page-titles">
        <div class="col-md-5 col-8 align-self-center">
          <ol class="breadcrumb">
            <li class="breadcrumb-item">
              <router-link to="/home">Home</router-link>
            </li>
            <li class="breadcrumb-item">
              <router-link to="/custom-service-code">サービス一覧</router-link>
            </li>
            <li class="breadcrumb-item active">サービス</li>
          </ol>
        </div>
      </div>
      <div class="row" v-if="errors && errors.length">
        <div class="col-md-12">
          <div class="alert alert-warning">
            <button type="button" class="close" data-dismiss="alert" aria-label="Close">
              <span aria-hidden="true">×</span>
            </button>
            <ul>
              <li v-for="(error, index) in errors" :key="index">
                {{ error }}
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col-md-12">
          <div class="card card-outline-info">
            <div class="card-header">
              <h4 class="m-b-0 text-white">サービス</h4>
            </div>
            <div class="card-body">
              <form action="#" class="form" :disabled="!isLoaded">
                <div class="form-body">
                    <!-- row -->
                    <div class="row">
                      <div class="col-md-3">
                        <div class="form-group" :class="{'has-danger': hasFieldError('display_name')}">
                          <label class="control-label">サービス名</label>
                          <input
                            type="text"
                            name="display_name"
                            class="form-control"
                            maxlength="30"
                            v-model="inputs.display_name"
                            @blur="validateField('display_name')"
                          >
                        </div>
                      </div>
                    </div>
                </div>
                <div class="form-actions">
                  <div class="row">
                    <div class="col-md-12 text-center">
                      <router-link to="/custom-service-code">キャンセル</router-link>
                      <span class="mx-2"></span>
                      <button
                        type="button"
                        class="action-button btn btn-danger btn-rounded"
                        @click.prevent="remove"
                        :disabled="!isLoaded"
                        v-if="id && !inputs.builtin_flag"
                      >
                        削除する
                      </button>
                      <button
                        type="submit"
                        class="action-button btn btn-success btn-rounded"
                        @click.prevent="save"
                        :disabled="!isLoaded"
                      >
                        保存する
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
    <loading-modal v-model:visible="isProcessing" message="更新中..."/>
    <teleport to="body">
      <CBModal ref="dialog" />
    </teleport>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import api from '@/libs/Api'
import LoadingModal from '@/components/LoadingModal.vue'
import CBModal from '@/components/CBModal.vue'
import { useToast } from 'vue-toastification'
import { useExecutor } from '@/libs/Executor'

const { exec } = useExecutor()

// Convert entity to inputs format
function _convertEntity2Inputs (entity) {
  return {
    display_name: entity.display_name,
    service_code: entity.service_code
  }
}

// Convert inputs to entity format
function _convertInputs2Entity (inputs) {
  return {
    display_name: inputs.display_name,
    service_code: inputs.service_code
  }
}

const router = useRouter()
const route = useRoute()
const toast = useToast()
const dialog = ref(null)

// State
const id = ref(null)
const isLoaded = ref(false)
const isProcessing = ref(false)
const inputs = reactive({
  display_name: '',
  service_code: ''
})
const errors = ref([])

// Form validation
function validateField (fieldName) {
  if (fieldName === 'display_name' && !inputs.display_name) {
    if (!errors.value.includes('サービス名は必須です。')) {
      errors.value.push('サービス名は必須です。')
    }
    return false
  }

  // Remove error if field is valid
  errors.value = errors.value.filter(err => !err.includes('サービス名'))
  return true
}

function validateForm () {
  errors.value = []
  let isValid = true

  if (!validateField('display_name')) {
    isValid = false
  }

  return isValid
}

function hasFieldError (fieldName) {
  if (fieldName === 'display_name') {
    return errors.value.some(err => err.includes('サービス名'))
  }
  return false
}

// Initialize component
id.value = route.params.id

// Load data
onMounted(async () => {
  exec(async () => {
    if (id.value) {
      const res = await api.findCustomServiceCode(id.value)
      if (res) {
        Object.assign(inputs, _convertEntity2Inputs(res))
        isLoaded.value = true
      } else {
        isLoaded.value = true
      }
    }
  }).finally(() => {
    isLoaded.value = true
  })
})

// Remove service
async function remove () {
  if (inputs.builtin_flag) return
  dialog.value.show({
    title: '確認',
    text: '削除してもよろしいですか？',
    buttons: [
      {
        title: 'キャンセル'
      },
      {
        title: 'はい',
        handler: async () => {
          exec(async () => {
            await api.deleteCustomServiceCode(id.value)
          }).then(() => {
            toast.success('削除しました。', {
              position: 'top-center',
              timeout: 2000
            })
            router.push('/custom-service-code')
          })
        }
      }
    ]
  })
}

// Save service
async function save () {
  exec(async () => {
    if (!validateForm()) {
      return
    }

    isProcessing.value = true
    const params = _convertInputs2Entity(inputs)

    let res
    if (id.value) {
      res = await api.putCustomServiceCode(id.value, params)
    } else {
      res = await api.postCustomServiceCode(params)
    }

    if (res) {
      toast.success('保存しました。', {
        position: 'top-center',
        timeout: 2000
      })
      Object.assign(inputs, _convertEntity2Inputs(res))
      id.value = res.id
    }
  }).finally(() => {
    isProcessing.value = false
  })
}
</script>

<style scoped>
</style>
